/**
 * `BriefSaveIndicator` — visual readout of the brief auto-save lifecycle.
 *
 * 5-state machine (`idle | unsaved | saving | saved | error`) derived in
 * the header from the form + mutation flags. Pure presentational.
 *
 * Visual contract:
 *   - `idle`    → render nothing (no clutter when there is nothing to report)
 *   - `unsaved` → muted "Unsaved changes" copy so the user knows a save is
 *                 pending in the debounce window
 *   - `saving`  → spinner + "Saving…"
 *   - `saved`   → check + "Saved" (with relative time when `savedAt` provided)
 *   - `error`   → destructive icon + "Failed to save: <message>" + Retry button
 *
 * NO React effect hook in this module.
 */
import { AlertCircle, Check, CircleDot } from 'lucide-react';

import { Button } from '@monksflow/monks-ui';

import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';

export type SaveState = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

export interface BriefSaveIndicatorProps {
  state: SaveState;
  /** Shown when `state === 'saved'`. Pass `null` to omit the relative time. */
  savedAt?: Date | null;
  /** Shown when `state === 'error'`. */
  error?: string | null;
  /** Wired when `state === 'error'`. When omitted, no Retry control is rendered. */
  onRetry?: () => void;
}

export function BriefSaveIndicator({ state, savedAt, error, onRetry }: BriefSaveIndicatorProps) {
  const { t } = useTranslation('projects');

  if (state === 'idle' || state === 'saving') return null;

  if (state === 'unsaved') {
    return (
      <span
        data-testid="brief-save-indicator"
        data-state="unsaved"
        className="flex items-center gap-1.5 text-sm text-muted-foreground"
      >
        <CircleDot className="size-3.5" aria-hidden />
        <span>{t('projectBriefDetail.briefSaveIndicator.unsavedChanges')}</span>
      </span>
    );
  }

  if (state === 'saved') {
    const relative = savedAt ? formatRelativeDate(savedAt) : '';
    return (
      <span
        data-testid="brief-save-indicator"
        data-state="saved"
        className="flex items-center gap-1.5 text-sm text-muted-foreground"
      >
        <Check className="size-3.5" aria-hidden />
        <span>
          {relative
            ? t('projectBriefDetail.briefSaveIndicator.savedRelative', { relative })
            : t('projectBriefDetail.briefSaveIndicator.saved')}
        </span>
      </span>
    );
  }

  // state === 'error'
  return (
    <span
      data-testid="brief-save-indicator"
      data-state="error"
      className="flex items-center gap-1.5 text-sm text-destructive"
    >
      <AlertCircle className="size-3.5" aria-hidden />
      <span>
        {error
          ? t('projectBriefDetail.briefSaveIndicator.failedToSaveWithError', { error })
          : t('projectBriefDetail.briefSaveIndicator.failedToSave')}
      </span>
      {onRetry && (
        <Button
          type="button"
          variant="ghost"
          size="mini"
          onClick={onRetry}
          data-testid="brief-save-indicator-retry"
        >
          {t('projectBriefDetail.briefSaveIndicator.retry')}
        </Button>
      )}
    </span>
  );
}
